import { useCallback, useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import Webcam from "react-webcam"
import { fetchSelfieInfo, uploadSelfie } from "../../services/selfieService"
import { fr } from "../../locales/fr"

/**
 * Page publique téléphone : selfie → upload Gymflow (token JWT court).
 * URL : /selfie?t=<jwt>
 */
export default function SelfieCapturePage() {
  const [params] = useSearchParams()
  const token = params.get("t") || ""
  const s = fr.selfie
  const webcamRef = useRef(null)
  const fileRef = useRef(null)

  const [info, setInfo] = useState(null)
  const [loadError, setLoadError] = useState("")
  const [cameraOk, setCameraOk] = useState(false)
  const [cameraFail, setCameraFail] = useState(false)
  const [preview, setPreview] = useState("")
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [uploadError, setUploadError] = useState("")

  useEffect(() => {
    if (!token) {
      setLoadError(s.invalidLink)
      return
    }
    let cancelled = false
    fetchSelfieInfo(token)
      .then((data) => {
        if (!cancelled) {
          setInfo(data)
          if (data.hasPhoto) setDone(true)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err.status === 410 ? s.expiredLink : s.invalidLink)
        }
      })
    return () => {
      cancelled = true
    }
  }, [token, s.invalidLink, s.expiredLink])

  const capture = useCallback(() => {
    const shot = webcamRef.current?.getScreenshot?.()
    if (shot) setPreview(shot)
  }, [])

  const onFile = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => setPreview(String(reader.result || ""))
    reader.readAsDataURL(file)
  }

  const send = async () => {
    if (!preview || !token) return
    setBusy(true)
    setUploadError("")
    try {
      await uploadSelfie(token, { photo: preview })
      setDone(true)
    } catch (err) {
      setUploadError(err.status === 410 ? s.expiredLink : s.uploadError)
    } finally {
      setBusy(false)
    }
  }

  if (loadError) {
    return (
      <Shell>
        <p className="text-center text-red-600">{loadError}</p>
      </Shell>
    )
  }

  if (!info) {
    return (
      <Shell>
        <p className="text-center text-stone-500">{s.loading}</p>
      </Shell>
    )
  }

  if (done) {
    return (
      <Shell>
        <h1 className="text-center text-2xl font-semibold text-stone-900">{s.thanksTitle}</h1>
        <p className="mt-2 text-center text-stone-600">
          {s.thanksBody.replace("{{name}}", info.firstName || "")}
        </p>
      </Shell>
    )
  }

  return (
    <Shell>
      <h1 className="text-center text-xl font-semibold text-stone-900">{s.pageTitle}</h1>
      <p className="mt-1 text-center text-sm text-stone-600">
        {s.pageHint.replace("{{name}}", info.firstName || s.memberFallback)}
      </p>

      <input ref={fileRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onFile} />

      {!preview ? (
        <div className="mt-5 space-y-3">
          {!cameraFail ? (
            <div className="relative aspect-[3/4] w-full overflow-hidden rounded-2xl bg-black">
              <Webcam
                ref={webcamRef}
                audio={false}
                mirrored
                screenshotFormat="image/jpeg"
                screenshotQuality={0.92}
                videoConstraints={{ facingMode: "user", width: 720, height: 960 }}
                onUserMedia={() => setCameraOk(true)}
                onUserMediaError={() => {
                  setCameraFail(true)
                  setCameraOk(false)
                }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          ) : (
            <div className="flex aspect-[3/4] items-center justify-center rounded-2xl border border-dashed border-stone-300 bg-stone-100 px-4 text-center text-sm text-stone-600">
              {s.cameraFail}
            </div>
          )}

          <div className="flex flex-col gap-2">
            {cameraOk && !cameraFail ? (
              <button
                type="button"
                onClick={capture}
                className="w-full rounded-xl bg-stone-900 px-4 py-3 text-base font-medium text-white"
              >
                {s.capture}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-base font-medium text-stone-800"
            >
              {s.pickGallery}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-5 space-y-3">
          <img
            src={preview}
            alt=""
            className="mx-auto aspect-[3/4] w-full max-w-sm rounded-2xl object-cover ring-1 ring-stone-200"
          />
          {uploadError ? <p className="text-center text-sm text-red-600">{uploadError}</p> : null}
          <button
            type="button"
            disabled={busy}
            onClick={send}
            className="w-full rounded-xl bg-emerald-700 px-4 py-3 text-base font-medium text-white disabled:opacity-60"
          >
            {busy ? s.sending : s.send}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setPreview("")
              setUploadError("")
            }}
            className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-base font-medium text-stone-800"
          >
            {s.retake}
          </button>
        </div>
      )}
    </Shell>
  )
}

function Shell({ children }) {
  return (
    <div className="min-h-dvh bg-gradient-to-b from-stone-100 via-amber-50/40 to-stone-100 px-4 py-8">
      <div className="mx-auto w-full max-w-md">
        <p className="mb-6 text-center text-lg font-semibold tracking-tight text-stone-800">Gymflow</p>
        {children}
      </div>
    </div>
  )
}
